import {
  CognitoUserSession,
  CognitoUser,
  CognitoUserPool,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import { Amplify, Auth } from "aws-amplify";

import { assertNativeError } from "@examspark/common";

import { safeSessionStorage } from "./safeStorage";
import { awsconfig } from "./utils";

const CLIENT_ID_CACHE_KEY = "CognitoIdentityServiceProvider.LastAuthClient";

export type AmplifyCognitoUser = CognitoUser & {
  attributes: {
    sub: string;
    email_verified?: boolean;
    email: string;
  };
  username: string;
  signInUserSession: CognitoUserSession;
};

export type SignInTokenParams = {
  clientId?: string;
  idToken?: string;
  accessToken?: string;
  refreshToken?: string;
};

export const createCognitoUser = async (email: string, password: string) => {
  reconfigureAmplifyAuthentication();

  return await Auth.signUp({
    username: email.toLowerCase(),
    password: password,
    attributes: {
      email: email.toLowerCase(),
    },
  });
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string,
) => {
  const user = await currentAuthenticatedUser();
  if (user == null) {
    console.error(`Attempted to change password with no logged-in user`);
    return;
  }
  await Auth.changePassword(user, oldPassword, newPassword);
};

export const confirmSignUp = async (email: string, code: string) => {
  reconfigureAmplifyAuthentication();

  try {
    return await Auth.confirmSignUp(email.toLowerCase().trim(), code.trim());
  } catch (error) {
    assertNativeError(error);
    if (!error.message.match(/CONFIRMED/)) {
      throw error;
    }
  }
};

export const signIn = async (
  email: string,
  password: string,
): Promise<AmplifyCognitoUser> => {
  reconfigureAmplifyAuthentication();

  const normalizedUsername = email.toLowerCase().trim();
  const result: AmplifyCognitoUser = await Auth.signIn({
    username: normalizedUsername, // Required, the username
    password: password, // Optional, the password
  });

  return result;
};

/** Creates an authenticated Cognito user client-side. */
export const signInToken = (params: SignInTokenParams) => {
  try {
    if (!params.clientId) throw new Error("Invalid client id.");
    if (!params.idToken) throw new Error("Invalid id token.");
    if (!params.accessToken) throw new Error("Invalid access token.");
    if (!params.refreshToken) throw new Error("Invalid refresh token.");
    if (!awsconfig.aws_user_pools_id) throw new Error("Invalid user pool id.");

    reconfigureAmplifyAuthentication(params.clientId);

    const userPool = new CognitoUserPool({
      UserPoolId: awsconfig.aws_user_pools_id,
      ClientId: params.clientId,
    });
    const cognitoIdToken = new CognitoIdToken({
      IdToken: params.idToken,
    });
    const cognitoAccessToken = new CognitoAccessToken({
      AccessToken: params.accessToken,
    });
    const cognitoRefreshToken = new CognitoRefreshToken({
      RefreshToken: params.refreshToken,
    });
    const username = cognitoIdToken.payload.email; // or what you use as username, e.g. email
    const user = new CognitoUser({
      Username: username,
      Pool: userPool,
    });
    user.setSignInUserSession(
      new CognitoUserSession({
        AccessToken: cognitoAccessToken,
        IdToken: cognitoIdToken,
        RefreshToken: cognitoRefreshToken,
      }),
    );

    return user;
  } catch (error) {
    reconfigureAmplifyAuthentication();
    console.error(error);
  }
};

export const signOut = async () => {
  reconfigureAmplifyAuthentication();

  return await Auth.signOut();
};

export const resendCode = async (email: string) => {
  reconfigureAmplifyAuthentication();
  return await Auth.resendSignUp(email.toLowerCase());
};

export function currentAuthenticatedUser(
  throwIfNotAuthenticated?: false,
): Promise<AmplifyCognitoUser | undefined>;
export function currentAuthenticatedUser(
  throwIfNotAuthenticated: true,
): Promise<AmplifyCognitoUser>;
export async function currentAuthenticatedUser(
  throwIfNotAuthenticated?: boolean,
): Promise<AmplifyCognitoUser | undefined> {
  reconfigureAmplifyAuthentication(
    safeSessionStorage.getItem(CLIENT_ID_CACHE_KEY) ?? undefined,
  );

  try {
    return await Auth.currentAuthenticatedUser();
  } catch (e) {
    if (e === "The user is not authenticated" && !throwIfNotAuthenticated) {
      reconfigureAmplifyAuthentication();
      return undefined;
    }
    throw e;
  }
}

export const reconfigureAmplifyAuthentication = (
  clientId = awsconfig.aws_user_pools_web_client_id,
) => {
  Amplify.configure({
    aws_user_pools_web_client_id:
      clientId ?? awsconfig.aws_user_pools_web_client_id,
    aws_user_pools_id: awsconfig.aws_user_pools_id,
  });

  safeSessionStorage.setItem(CLIENT_ID_CACHE_KEY, clientId);
};
